import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, ChevronRight } from "lucide-react";
import Logo from "@/components/Logo";

// Ceremonial Acknowledgement screen — appears BEFORE the Girribal Gate.
// Quiet, deliberate, no nav, no banner. Visitor must consciously continue.

const FADE_IN = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: "easeOut" },
});

export default function AcknowledgementCeremony({ onContinue }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Continue button revealed after the words have been given time to land.
    const t = setTimeout(() => setReady(true), 3200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-stone-900 relative overflow-hidden flex items-center" data-testid="acknowledgement-screen">
      {/* Soft warm gradient wash — kept subtle so the words remain primary */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-[42rem] h-[42rem] rounded-full bg-[#9C4221]/[0.06] blur-[120px]" />
        <div className="absolute -bottom-40 -left-32 w-[36rem] h-[36rem] rounded-full bg-stone-300/40 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-[860px] px-6 md:px-10 py-16 md:py-20 w-full">
        {/* Branding mark */}
        <motion.div {...FADE_IN(0)}>
          <Logo />
        </motion.div>

        <motion.div {...FADE_IN(0.35)} className="mt-12 inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase font-bold text-[#9C4221]">
          <Heart className="h-3 w-3" strokeWidth={2.5} />
          Acknowledgement of Country
        </motion.div>

        <motion.h1
          {...FADE_IN(0.55)}
          className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-bold text-stone-900 leading-[1.1] max-w-3xl"
        >
          Before anything else, we pause.
        </motion.h1>

        <motion.div {...FADE_IN(0.85)} className="mt-8 space-y-6 text-stone-800 leading-[1.85] text-base md:text-lg max-w-2xl">
          <p>
            BIGIBILA SYSTEMS™ acknowledges the Traditional Custodians of the lands across this continent and its islands — and the deep, unbroken connection First Nations peoples hold with Country, waters, sky and Lore.
          </p>
          <p>
            We pay our profound respect to <span className="font-semibold text-stone-900">those who came before us</span> — the Ancestors, Elders, knowledge holders and Lore carriers whose strength and protection has carried language, law and culture through generations.
          </p>
          <p>
            We honour <span className="font-semibold text-stone-900">those leading now</span> — Elders, Custodians, advisors and community members holding ground in the present.
          </p>
          <p>
            We hold space for <span className="font-semibold text-stone-900">those emerging</span> — the young leaders, students, artists and future Custodians whose work will outlive ours.
          </p>
        </motion.div>

        <motion.div
          {...FADE_IN(1.15)}
          className="mt-12 border-l-2 border-[#9C4221]/40 pl-6 max-w-2xl"
        >
          <p className="font-display italic text-stone-700 text-lg md:text-xl leading-relaxed">
            Sovereignty was never ceded.
          </p>
          <p className="mt-3 text-sm text-stone-600 leading-relaxed">
            Always was. Always will be Aboriginal land.
          </p>
        </motion.div>

        {/* Continue gate */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: ready ? 1 : 0 }}
          transition={{ duration: 0.7 }}
          className="mt-14"
        >
          <button
            type="button"
            onClick={onContinue}
            disabled={!ready}
            data-testid="acknowledgement-continue"
            aria-label="Acknowledge and continue"
            className="group inline-flex items-center gap-3 bg-stone-900 text-white px-7 py-4 text-sm font-medium tracking-wide rounded-sm hover:bg-stone-800 transition-all disabled:opacity-0"
          >
            I acknowledge — continue
            <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="mt-5 text-[11px] tracking-wide text-stone-500 italic max-w-md leading-relaxed">
            Taking a moment here is the first act of respect on this site.
          </p>
        </motion.div>

        {/* Quiet footer line */}
        <motion.div {...FADE_IN(1.6)} className="mt-16 pt-8 border-t border-stone-200 flex flex-wrap items-center justify-between gap-3 text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500">
          <span>BIGIBILA SYSTEMS™</span>
          <span>Strong Foundations · Living Governance · Sovereign Futures</span>
        </motion.div>
      </div>
    </div>
  );
}
